import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  MenuItem,
  Divider,
  FormLabel,
  FormControl,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { Button, Textarea } from '~/components';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';

interface CancelContractExtensionProps {
  id_contrato: string;
  isDisabled?: boolean;
}

type FormData = {
  motivo: string;
};

export const CancelContractExtension: React.FC<CancelContractExtensionProps> =
  ({ id_contrato, isDisabled }: CancelContractExtensionProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const {
      register,
      handleSubmit,
      setValue,
      setError,
      formState,
      clearErrors,
      control,
    } = useForm();
    const { errors } = formState;

    const [isFetching, setFetching] = useState(false);

    const {
      isOpen: isOpenModal,
      onOpen: onOpenModal,
      onClose: onCloseModal,
    } = useDisclosure();

    const handleOpen = () => {
      onOpen();
    };

    const handleClose = () => {
      clearValues();
      onClose();
    };

    const clearValues = () => {
      setValue('motivo', '');
      clearErrors();
    };

    const validate = (form: FormData) => {
      const { motivo } = form;

      clearErrors();
      !motivo.trim() &&
        setError('motivo', {
          type: 'required',
          message: 'Motivo obrigatório',
        });
      motivo.trim().length > 500 &&
        setError('motivo', {
          type: 'maxLength',
          message: 'Máximo de 500 caracteres',
        });

      const { _formState } = control;
      return _formState.isValid;
    };

    async function handleSubmitForm(form: FormData) {
      if (!validate(form)) return;

      setFetching(true);
      const request = api.put(`/cancelamento_extensao/${id_contrato}/`, form);
      request
        .then(() => {
          toast({
            title: 'Extensão cancelada com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          clearValues();
          onClose();
          queryClient.invalidateQueries(['contract', id_contrato]);
          queryClient.invalidateQueries(['contract', 'active']);
          window.location.reload();
        })
        .catch(error => {
          if (error.response.status === 403) {
            toast({
              title: 'Usuário sem permissão',
              description: 'Sem permissão para execução da operação.',
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          } else if (error.response.status === 400) {
            toast({
              title: `Erro ao cancelar extensão`,
              description: error.response.data,
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          } else {
            toast({
              title: `Erro ao cancelar extensão`,
              description: 'Tente novamente',
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        })
        .finally(() => {
          setFetching(false);
        });
    }

    return (
      <>
        <MenuItem fontWeight="bold" onClick={onOpen} isDisabled={isDisabled}>
          Cancelar extensão
        </MenuItem>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="lg"
          closeOnOverlayClick={false}
        >
          <DrawerOverlay />
          <DrawerContent borderTopLeftRadius="md">
            <DrawerCloseButton isDisabled={isFetching} />
            <DrawerHeader ml="1" mb={4}>
              Cancelar extensão
            </DrawerHeader>
            <DrawerBody mx="1">
              <Stack
                gridGap={6}
                bg="white"
                w={600}
                as="form"
                onSubmit={handleSubmit(handleSubmitForm)}
              >
                <FormControl isInvalid={errors.motivo} isRequired>
                  <FormLabel fontSize="14px">
                    Compartilhe o motivo do cliente ter solicitado o
                    cancelamento
                  </FormLabel>
                  <Textarea
                    name="motivo"
                    resize="none"
                    error={errors?.motivo}
                    register={register}
                    fontSize="15px"
                    isDisabled={isFetching}
                  />
                </FormControl>
                <Divider mt={4} mb={4} />
                <Text fontSize="14px">
                  Ao confirmar, enviaremos um e-mail ao Cliente informando que a
                  solicitação de extensão ou encerramento foi cancelada.
                </Text>
                <HStack justify="flex-end">
                  <Button
                    onClick={handleClose}
                    variant="secondary"
                    isDisabled={isFetching}
                    style={{ marginTop: '20px' }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    style={{ marginTop: '20px' }}
                    isDisabled={isFetching}
                  >
                    Confirmar{' '}
                    {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
                  </Button>
                </HStack>
              </Stack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  };
